import { API } from 'aws-amplify';
import moment from 'moment';

const API_ENDPOINT = 'https://twu6cnt640.execute-api.us-east-1.amazonaws.com/latest/data';

/**
 * DataAPI class implements a set of custom functions that format the data
 * provided by the database to the correct format based on the feature that the
 * data is going to serve to.
 *
 * It uses the Auth and API libraries of AWS Amplify framework.
 *
 * @class DataAPI
 */
export default class DataAPI {
    defaultResult = {
        chats: {},
            existingVsNewOnes: {},
            stats: {},
            questions: {},
            locations: {
                locations: [],
                topLocations: []
            },
            devices: {},
            mostCommonlyQuestion: ''
    };
    static defaultWebMDStatsResult = {
        statistics: {
            visibility_statistics: {
                by_month: {},
                by_month_accumulative: {},
                by_day_of_week: {},
                by_type: {}
            },
            contact_statistics: {
                by_month: {},
                by_month_accumulative: {},
                by_day_of_week: {},
                by_type: {}
            }
        },
        percentages: {
            visibility: {},
            contact: {}
        },
        totalRecords: {
            visibility: null,
            contact: null
        },
        chartData: {
            visibility: {},
            contact: {}
        }
    };
    static defaultTextToPayDataResult = {
        kpis: {
            total_payment_requests: 0,
            total_payments: 0,
            amount_paid: 0,
            average_amount_paid: 0
        },
        payments_statistics: {
            by_month: {},
            by_month_accumulative: {},
            by_day_of_week: {}
        }
    };
    static defaultAIAutoDialerDataResult = {
        kpis: {
            total_dials: 0,
            unique_callers: 0
        },
        dials_statistics: {
            by_month: {},
            by_month_accumulative: {},
            by_day_of_week: {},
            by_hour: {}
        }
    };
    static defaultAppointmentsDataResult = {
        total_appointments: 0,
        booked_appointments: 0,
        showed_appointments: 0,
        noshow_appointments: 0,
        cancelled_appointments: 0,
        deleted_appointments: 0,
        confirmed_appointments: 0,
        appointments_stats: {},
    };

    static validParams(params, report) {
        const current = Object.keys(params);
        let required = report ?
            [ "clinic", "apiKey", "frecuency", "cumulative", "from", "to" ] :
            [ "account", "clinic", "location", "from", "to" ];

        return current.every(param => required.includes(param));
    }

    static async getData(params, report = false) {
        if (this.validParams(params, report)) {
            console.info(`[${report ? "report" : "web"}] DataAPI: getting data for...`, params);

            const data = report ?
                await this.loadReport(params) :
                await this.load(params);

            console.info(`[${report ? "report" : "web"}] DataAPI: data received...`, data);

            return !data ? this.defaultResult : {
                clinic: this.getClinic(data),
                clinicLocations: data.office_locations_available ? data.office_locations_available : [],
                currentClinicLocation: data.office_location,
                chats: this.getChats(data),
                existingVsNewOnes: this.getExistingVsNewOnes(data),
                stats: this.getStats(data),
                questions: this.getQuestions(data),
                locations: {
                    locations: !report ? await this.getLocations(data): [],
                    topLocations: this.getTopTenLocations(data)
                },
                devices: this.getDevices(data),
                mostCommonlyQuestion: this.getMostCommonlyQuestion(data)
            }
        } else return this.defaultResult;
    }

    static async getReviewsData(clinic, report = false) {
        if (clinic) {
            console.info(`[${report ? "report" : "web"}] DataAPI: getting reviews data for...`, clinic);
            const query = { ...clinic };

            const data = report ?
              await this.loadReportGeneric({path: "/statistics/reviews", query}) :
              await this.loadGeneric({path: "/statistics/reviews", query});

            console.info(`[${report ? "report" : "web"}] DataAPI: reviews data received...`, data);
            return data;
        } else return this.defaultResult;
    }

    static async findWebMDProperty(clinic) {
        if(!clinic) return;
        try {
            console.info(`[web] DataAPI: getting webmd property key for...`, clinic);
            const data = await API.post("chatsapi", "/webmd/find", {body: {clinic: clinic}});
            console.info(`[web] DataAPI: data received...`, data);
            return data;
        } catch (e) {
            return;
        }
    }


    static async getWebMDData(propertyKey) {
        if(!propertyKey) return;
        console.info(`[web] DataAPI: getting webmd data for...`, propertyKey);
        const data =  await API.post("chatsapi", "/webmd/stats", {body: {property_key: Number(propertyKey)}});
        console.info(`[web] DataAPI: data received...`, data);
        return data;
    }

    static async getWebMDStats(params, report = false) {
        if (!this.validParams(params, report)) {
            return this.defaultWebMDStatsResult;
        }
        console.info(`[${report ? "report" : "web"}] DataAPI: getting WebMD data for...`, params.clinic);

        const activeProducts = await this.getActiveProducts(params.clinic);
        const webmdNpi = activeProducts?.metadata?.webmd_npi;
        const accountId = activeProducts?.metadata?.account_id;
        if (!webmdNpi && !accountId) {
            console.error(`[web] DataAPI: webmd_npi and account_id not found`);
            return this.defaultWebMDStatsResult;
        }

        console.info(`[web] DataAPI: getting WebMD stats for...`, webmdNpi || accountId);
        const query = { clinic: params.clinic };
        if (webmdNpi) query.webmd_npi = webmdNpi;
        if (accountId) query.account_id = accountId;
        const data = report ?
            await this.loadReportGeneric({path: "/webmddata/stats", query}) :
            await this.loadGeneric({path: "/webmddata/stats", query});

        console.info(`[web] DataAPI: data received...`, data);

        return !data ? this.defaultWebMDStatsResult : {
            statistics: data,
            totalRecords: this.getWebMDTotalCount(data),
            percentages: this.getWebMDMetricsPercentages(data),
            chartData: this.getWebMDChartData(data)
        };
    }

    static getWebMDTotalCount(data) {
        const { contact_statistics, visibility_statistics } = data;
        if (!contact_statistics || !visibility_statistics) return {};
        const contactStats = contact_statistics.by_type;
        const visibilityStats = visibility_statistics.by_type;

        return {
            contact: Object.values(contactStats).reduce((prev, curr) => prev + Number(curr)),
            visibility: Object.values(visibilityStats).reduce((prev, curr) => prev + Number(curr))
        };
    }

    static getWebMDMetricsPercentages(data) {
        const { contact_statistics, visibility_statistics } = data;
        if (!contact_statistics || !visibility_statistics) return {};

        const contactStats = contact_statistics.by_type;
        const visibilityStats = visibility_statistics.by_type;

        const totalContactStats = Object.values(contactStats).reduce((prev, curr) => prev + Number(curr));
        const totalVisibilityStats = Object.values(visibilityStats).reduce(
            (prev, curr) => prev + Number(curr)
        );

        return {
            contact: {
                WebsiteClicks: {
                    percentage: (Number(contactStats.website_clicks) / totalContactStats * 100).toFixed(1) || 0,
                    count: Number(contactStats.website_clicks)
                },
                Calls: {
                    percentage: (Number(contactStats.calls) / totalContactStats * 100).toFixed(1) || 0,
                    count: Number(contactStats.calls)
                },
                ChatNowClicks: {
                    percentage: (Number(contactStats.chatnow_clicks) / totalContactStats * 100).toFixed(1) || 0,
                    count: Number(contactStats.chatnow_clicks)
                }
            },
            visibility: {
                CompetitorImpressions: {
                    percentage: (Number(visibilityStats.competitor_impressions) / totalVisibilityStats * 100).toFixed(1) || 0,
                    count: Number(visibilityStats.competitor_impressions)
                },
                Impressions: {
                    percentage: (Number(visibilityStats.impressions) / totalVisibilityStats * 100).toFixed(1) || 0,
                    count: Number(visibilityStats.impressions)
                },
                ProfileViews: {
                    percentage: (Number(visibilityStats.profile_views) / totalVisibilityStats * 100).toFixed(1) || 0,
                    count: Number(visibilityStats.profile_views)
                }
            }
        };
    }

    static getWebMDChartData(data) {
        const { contact_statistics, visibility_statistics } = data;
        if (!contact_statistics || !visibility_statistics) return {};

        let contact = {
            day: contact_statistics.by_day_of_week,
            month: {},
            month_cumulative: {}
        }

        Object.keys(contact_statistics.by_month).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            contact.month[month] = contact_statistics.by_month[rawMonth]
            contact.month_cumulative[month] = contact_statistics.by_month_accumulative[rawMonth]
        });

        let visibility = {
            day: visibility_statistics.by_day_of_week,
            month: {},
            month_cumulative: {}
        }

        Object.keys(visibility_statistics.by_month).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            visibility.month[month] = visibility_statistics.by_month[rawMonth]
            visibility.month_cumulative[month] = visibility_statistics.by_month_accumulative[rawMonth]
        });

        return {
            contact,
            visibility
        }
    }

    static async getTextToPayData(params, report = false) {
        if (!this.validParams(params, report)) {
            return this.defaultTextToPayDataResult;
        }
        console.info(`[${report ? "report" : "web"}] DataAPI: getting TextToPay data for...`, params.clinic);

        const activeProducts = await this.getActiveProducts(params.clinic);
        const accountId = activeProducts?.metadata?.account_id;
        const query = { ...params };
        if (accountId) query.account_id = accountId;

        const data = report ?
            await this.loadReportGeneric({path: "/texttopay/stats", query}) :
            await this.loadGeneric({path: "/texttopay/stats", query});

        let formattedPaymentsStatistics = {
            by_month: {},
            by_month_accumulative: {},
            by_day_of_week: {}
        };
        Object.keys(data.payments_statistics.by_month).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            formattedPaymentsStatistics.by_month[month] = data.payments_statistics.by_month[rawMonth]
            formattedPaymentsStatistics.by_month_accumulative[month] = data.payments_statistics.by_month_accumulative[rawMonth]
        });

        console.info(`[${report ? "report" : "web"}] DataAPI: data received...`, data);

        return !data ? this.defaultTextToPayDataResult : {
            kpis: data.kpis,
            payments_statistics: formattedPaymentsStatistics
        };
    }

    static async getAIAutoDialerData(params, report = false) {
        if (!this.validParams(params, report)) {
            return this.defaultTextToPayDataResult;
        }
        console.info(`[${report ? "report" : "web"}] DataAPI: getting AI Auto Dialer data for...`, params.clinic);

        const data = report ?
            await this.loadReportGeneric({path: "/aiautodialer/stats", query: params}) :
            await this.loadGeneric({path: "/aiautodialer/stats", query: params});

        let formattedDialsStatistics = {
            by_month: {},
            by_month_accumulative: {},
            by_day_of_week: data.dials_statistics.by_day_of_week,
            by_hour: data.dials_statistics.by_hour,
        };
        Object.keys(data.dials_statistics.by_month).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            formattedDialsStatistics.by_month[month] = data.dials_statistics.by_month[rawMonth]
            formattedDialsStatistics.by_month_accumulative[month] = data.dials_statistics.by_month_accumulative[rawMonth]
        });

        console.info(`[${report ? "report" : "web"}] DataAPI: data received...`, data);

        return !data ? this.defaultTextToPayDataResult : {
            kpis: data.kpis,
            dials_statistics: formattedDialsStatistics
        };
    }

    static async getAppointmentsData(params) {
        const data = await API.get("appointmentsapi", "/appointments/stats", { queryStringParameters: params});
        console.info("[web] DataAPI: data received...", data);
        return data ?? this.defaultAppointmentsDataResult;
    }

    static async getActiveProducts(clinic) {
        if(!clinic) return;
        console.info(`[web] DataAPI: getting active products for...`, clinic);
        const data =  await API.get("chatsapi", "/products", {queryStringParameters: {clinic}});
        console.info(`[web] DataAPI: data received...`, data);
        return data;
    }

    static async getActiveProductsByIdentifier(identifier, paramType = 'account_id') {
        if(!identifier) return;
        console.info(`[web] DataAPI: getting active products by ${paramType}:`, identifier);
        const queryParams = paramType === 'account_id'
            ? {account_id: identifier}
            : {ghl_key: identifier};
        const data = await API.get("chatsapi", "/products", {queryStringParameters: queryParams});
        console.info(`[web] DataAPI: data received...`, data);
        return data;
    }

    static async load({ account = false, clinic = false, location = false, from = false, to = false }) {
        if (clinic || (from && to)) {
            const params = { queryStringParameters: {} };
            if (clinic) params.queryStringParameters.clinic = clinic;
            if (account) params.queryStringParameters.account = account;
            if (location && location != 'all') params.queryStringParameters.office_location = location;
            if (from && to) {
                params.queryStringParameters.from = new Date(from).toISOString();
                params.queryStringParameters.to = new Date(to).toISOString();
            }
            return await API.get("chatsapi", "/chats", params);
        } else return await API.get("chatsapi", "/chats");
    }

    static async loadGeneric({ path, query }) {
        const params = { queryStringParameters: query };
        return await API.get("chatsapi", path, params);
    }

    static async notifyUpselling(name, email, clinic) {
        try {
            let endpoint = '/chats/slack';
            let params = {};
            params.body = {
                name, email, clinic
            }
            console.log(params);
            return await API.post("chatsapi", endpoint, params);
        } catch (e) {
            return;
        }
    }


    static async loadReport({ clinic, apiKey, frecuency, cumulative, from, to }) {
        if (
            !clinic || !apiKey ||
            !['monthly', 'weekly', 'daily', ''].includes(frecuency) ||
            !['false', 'true'].includes(cumulative)
        ) return [];

        const method = 'POST';
        const headers = {
            'Content-Type': 'application/json',
            'X-Api-Key': apiKey
        };
        const body = JSON.stringify({ clinic, frecuency, from, to, accumulated: cumulative });
        const res = await fetch(API_ENDPOINT, { method, headers, body })
        return res.json();
    }

    static async loadReportGeneric({ path, query, apiKey}) {
        const method = 'GET';
        const headers = {
            'Content-Type': 'application/json',
            'X-Api-Key': apiKey
        };
        console.log(query)
        const searchParams = new URLSearchParams(query);
        const res = await fetch(API_ENDPOINT + path + `?${searchParams.toString()}`, { method, headers })
        return res.json();
    }


    static async getClinics(search = false) {
        try {
            const params = search ? { queryStringParameters: { search } } : {};
            return await API.get("chatsapi", "/clinics", params);
        } catch {
            return false;
        }
    }

    static getClinic(data) {
        return data.clinic;
    }

    static getStats(data) {
        const { overall_statistics } = data;
        if (!overall_statistics) return;
        return {
            averageTimeSpent: Math.round(overall_statistics.average_time_spent),
            conversionRate: (overall_statistics.conversion_rate * 100).toFixed(1),
            numberOfChats: {
                total: new Intl.NumberFormat("en-EN", {useGrouping: true}).format(overall_statistics.number_of_chats.total),
                last_month: new Intl.NumberFormat("en-EN", {useGrouping: true}).format(overall_statistics.number_of_chats.last_month)
            },
            numberOfReferrals: {
                total: new Intl.NumberFormat("en-EN", {useGrouping: true}).format(overall_statistics.number_of_referrals.total),
                last_month: new Intl.NumberFormat("en-EN", {useGrouping: true}).format(overall_statistics.number_of_referrals.last_month)
            },
            referralGrowth: overall_statistics.referral_growth.toFixed(1),
            newPatients: (overall_statistics.new_patients * 100).toFixed(1),
            totalTimeOnChat: (overall_statistics.total_time_spent / 60).toFixed(0)
        }
    }

    static getExistingVsNewOnes(data) {
        const { overall_statistics } = data;
        if (!overall_statistics) return;

        return {
            News: (overall_statistics.new_patients * 100).toFixed(1),
            Existing: ((1 - overall_statistics.new_patients) * 100).toFixed(1)
        }
    }

    static getChats(data) {
        const { chat_statistics } = data;
        if (!chat_statistics) return;

        let result = {
            hour: chat_statistics.by_hour,
            day: chat_statistics.by_day_of_week,
            month: {},
            month_cumulative: {}
        }

        Object.keys(chat_statistics.by_month).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            result.month[month] = chat_statistics.by_month[rawMonth]
            result.month_cumulative[month] = chat_statistics.by_month_accumulative[rawMonth]
        });

        return result;
    }

    static getChatsCumulative(data) {
        const { chat_statistics } = data;
        if (!chat_statistics) return;

        let result = {}

        Object.keys(chat_statistics.by_month_accumulative).forEach(rawMonth => {
            const month = moment(rawMonth).format("MMM YYYY");
            result[month] = chat_statistics.by_month_accumulative[rawMonth]
        });

        return result;
    }

    static getQuestions(data) {
        return data.top_questions;
    }

    static getMostCommonlyQuestion(data) {
        return Object.keys(data.top_questions)[0];
    }

    static async getLocations(data) {
        const { top_locations } = data;
        if (!top_locations) return;

        const locationsNames = Object.keys(top_locations).filter(location => top_locations[location] > 10);
        const locationsCoords = await API.post("locationsapi", "/locations", {
            body: locationsNames
        });

        const results = [];
        const { length } = locationsNames;
        for (let i = 0; i < length; i++) {
            const location = locationsNames[i];

            results.push({
                title: location,
                coordinates: locationsCoords[location],
                chats: top_locations[location]
            });
        }

        return results;
    }

    static getTopTenLocations(data) {
        const { top_locations } = data;
        if (!top_locations) return;
        const places = Object.keys(top_locations);

        const results = [];
        const { length } = places;
        for (let i = 0; i < length; i++) {
            const label = places[i];
            const value = top_locations[label];
            results.push({ label, value });
        }

        return results.sort((a, b) => b.value - a.value).slice(0, 10);
    }

    static getDevices(data) {
        const { device_type_statistics } = data;
        if (!device_type_statistics) return {};

        const total = Object.values(device_type_statistics).reduce((prev, curr) => prev + Number(curr));

        return {
            Desktop: (Number(device_type_statistics.computer) / total * 100).toFixed(1) || 0,
            Mobile: (Number(device_type_statistics.phone) / total * 100).toFixed(1) || 0,
            Tablet: (Number(device_type_statistics.tablet) / total * 100).toFixed(1) || 0,
            Other: (Number(device_type_statistics.othe) / total * 100).toFixed(1) || 0
        };
    }
}
